import { sprite, type SpriteDef } from './types';

// LED-matrix art for the Maw's 64×24 display (docs/07): Collector portraits that take over the display
// during Reckonings, plus extra Maw expressions. Same encoding as face.ts:
// '#' lit LED, 'e' eye interior (pupil travel area), 'p' pupil (centered gaze; the renderer shifts it),
// 'r' red LED accent, '.' unlit LED.
const LED_COLORS = { '#': 'brass', p: 'gold', e: 'aubergine', r: 'red' } as const;

export const PORTRAIT_SPRITES: SpriteDef[] = [
  // The Auditor: tight bun, half-moon specs with a glint, pencil behind the ear. No face to see.
  sprite('portrait_auditor', { ...LED_COLORS }, [
    '............................########............................',
    '...........................##......##...........................',
    '...........................#.######.#...........................',
    '............................##....##............................',
    '.........................##..######..##.........................',
    '........................####........####...rr...................',
    '.......................####..######..####...##..................',
    '......................####.##########.####..##..................',
    '.....................####.############.####..##.................',
    '.....................#....................#..##.................',
    '.................#..########################..##................',
    '................###..#eeeeeeee#..#eeeeeeee#....#................',
    '.................#...#eeeppeee#..#eeeppeee#.....................',
    '.....................#eeeppeee#..#eeeppeee#.....................',
    '......................#eeeeee#....#eeeeee#......................',
    '.......................######......######.......................',
    '...................r........................r...................',
    '...............................##...............................',
    '................................................................',
    '.............................rrrrrr.............................',
    '................................................................',
    '..........................#.#.#..#.#.#..........................',
    '..........#####################rr#####################..........',
    '....######............................................######....',
  ]),
  // The Glutton: huge round face, tiny eyes, enormous drooling mouth; fork and knife at the ready.
  sprite('portrait_glutton', { ...LED_COLORS }, [
    '................................................................',
    '.#.#.#.#................################.................#......',
    '.#.#.#.#.............###................###..............##.....',
    '.#.#.#.#..........###......................###...........#.#....',
    '.#.#.#.#........##..#######..........#######..##.........#.#....',
    '.#######.......#...#eeeeeee#........#eeeeeee#...#........#..#...',
    '..#####.......#....#eeepeee#..##....#eeepeee#....#.......#..#...',
    '...###.......#.....#eeeeeee#.#..#...#eeeeeee#.....#......#..#...',
    '....#.......#.......#######...##.....#######.......#.....#..#...',
    '...###......#...#..............................#...#.....#..#...',
    '...###.....#...#......##.##############.##......#...#....####...',
    '...###.....#..#.......#####.##....##.#####.......#..#.....##....',
    '...###.....#..#......##..................##......#..#.....##....',
    '...###....#...#.....#......................#.....#...#....##....',
    '...###.....#...#...#........................#...#...#.....##....',
    '...###.....#....#..#........................#..#....#.....##....',
    '...###.....#......#..........................#......#.....##....',
    '...###......#......#........................#......#......##....',
    '...###......#......#........................#......#......##....',
    '...###.......#......#.....rrrrrrrrrrrr.....#......#.......##....',
    '...###........#......##.rrrrrrrrrrrrrrrr.##......#........##....',
    '...###.........#.....#.###rrrrrrrrrrrr####......#.........##....',
    '...###..........##...#....############...#....##..........##....',
    '..................#####....................###..................',
  ]),
  // The Hex Widow: mourning hat and veil, cursed sigil on the brow, a web and its spider.
  sprite('portrait_widow', { ...LED_COLORS }, [
    '.......................##################.......................',
    '......................####################......................',
    '......................##................##......................',
    '......................####################......................',
    '....########################################################....',
    '...#......#..............##..........##..............#......#...',
    '..........#.............#....r.rr.r....#.............#..........',
    '..........#.........#..#..#...rrrr..#...#.#.#........#..........',
    '..........#............#.......rr.......#............#..........',
    '....#....###....#...#.########....########..#........#..........',
    '.....#.##.#.##.#......#########..#########...........#..........',
    '.....##...#...##....#.#eeeeeee#..#eeeeeee#..#........#..........',
    '.....#.#.##..#.#......#eeepeee#..#eeepeee#...........#..........',
    '.....#..#.###...#...#.#eeeeeee#..#eeeeeee#..#........#..........',
    '....#...####.#..#.....########....########.......#..#.#..#......',
    '.####################.#.........#........#..#.....#.###.#.......',
    '....#..#.####...#.....#..................#.........#####........',
    '....#...###.#..#......#.......r..r.......#........#.###.#.......',
    '.....#.#..##.#.#.......#.....rrrrrr.....#........#..#.#..#......',
    '.....##...#...##........#.....rrrr.....#........................',
    '.....#.##.#.##.#.........#............#.........................',
    '....#....###....#.....#.#.#.#.#..#.#.#.#.#......................',
    '..........#...........####################......................',
    '..........############....................############..........',
  ]),
  // The Twins: two identical bob-haired faces, each with its own pair of eyes.
  sprite('portrait_twins', { ...LED_COLORS }, [
    '..............####............................####..............',
    '..........#####..#####....................#####..#####..........',
    '.........##############..................##############.........',
    '.......##################..............##################.......',
    '......##.##.##.##.##.##.##............##.##.##.##.##.##.##......',
    '.....#....................#..........#....................#.....',
    '.....#.#######....#######.#..........#.#######....#######.#.....',
    '....###eeeeeee#..#eeeeeee###........###eeeeeee#..#eeeeeee###....',
    '....###eeepeee#..#eeepeee###........###eeepeee#..#eeepeee###....',
    '....###eeeeeee#..#eeeeeee###........###eeeeeee#..#eeeeeee###....',
    '....##.#######....#######.##........##.#######....#######.##....',
    '....##....................##........##....................##....',
    '....##.rr..............rr.##........##.rr..............rr.##....',
    '....##......#......#......##........##......#......#......##....',
    '....##.......######.......##........##.......######.......##....',
    '....###..................###........###..................###....',
    '....##.#................#.##........##.#................#.##....',
    '........#..............#................#..............#........',
    '.........##############..................##############.........',
    '........####..rrrr..####................####..rrrr..####........',
    '.......#....#.r##r.#....#..............#....#.r##r.#....#.......',
    '......#......#rrrr#......#............#......#rrrr#......#......',
    '......#......##..##......#............#......##..##......#......',
    '.......######......######..............######......######.......',
  ]),
  // The Taxman: bowler hat, pince-nez, pencil mustache, flanked by a square root and a percent.
  sprite('portrait_taxman', { ...LED_COLORS }, [
    '................................................................',
    '............................########............................',
    '..........................############..........................',
    '.........................##############.........................',
    '........................################........................',
    '................................................................',
    '....................#...################...#....................',
    '........rrrrrr.......######################..........rr.....r...',
    '........r..............#................#...........r..r...r....',
    '.......r...............#######....#######...........r..r..r.....',
    '.......r..............#eeeeeee####eeeeeee#...........rr..r......',
    '...r...r..............#eeepeee#..#eeepeee#..............r.......',
    '...rr.r...............#eeeeeee#..#eeeeeee#.............r..rr....',
    '....r.r................#######....#######.............r..r..r...',
    '....rr.................#.......##.......#............r...r..r...',
    '.....r.................#......#..#......#...........r.....rr....',
    '........................#..............#........................',
    '........................################........................',
    '.........................#...######...#.........................',
    '..........................##........##..........................',
    '............................########............................',
    '.........................###........###.........................',
    '............###########.#.####.##.####.#.###########............',
    '.......#####...........#..##........##..#...........#####.......',
  ]),
  // The Mirror Man: a looking-glass face split by the mirror line; the right half mirrors the left.
  sprite('portrait_mirrorman', { ...LED_COLORS }, [
    '............................########............................',
    '........................####...##...####........................',
    '......................##....###..###....##......................',
    '.....#..............##...###...##...###...##..............#.....',
    '....###............#...##...##....##...##...#............###....',
    '.....#............#...#...##...##...##...#...#............#.....',
    '.................#...#.####..........####.#...#.................',
    '................#...#..........##..........#...#................',
    '................#....########......########....#................',
    '...............#....#eeeeeeee#.##.#eeeeeeee#....#...............',
    '.............#.#....#eeeppeee#....#eeeppeee#....#.#.............',
    '............#.##....#eeeeeeee#.##.#eeeeeeee#....##.#............',
    '..#..........#.#.....########......########.....#.#..........#..',
    '...............#...............##...............#...............',
    '...............#............#......#............#...............',
    '................#....#.........##.........#....#................',
    '................#.....#########..#########.....#................',
    '........#........#.....#.#.#.#.##.#.#.#.#.....#........#........',
    '.......#.#........#.....#######..#######.....#........#.#.......',
    '........#..........##..........##..........##..........#........',
    '.....................##..................##.....................',
    '.......................##......##......##.......................',
    '.........................######..######.........................',
    '................................................................',
  ]),
  // The Moth Queen: a small crowned face framed by huge eyespotted wings and feathery antennae.
  sprite('portrait_mothqueen', { ...LED_COLORS }, [
    '..................######................######..................',
    '..#########......#.###..##............##..###.#......#########..',
    '.#.........####.......##..#..#....#..#..##.......####.........#.',
    '.#.............###......##.#.#.##.#.#.##......###.............#.',
    '#..........###....##.......#.######.#.......##....###..........#',
    '#........##...##....##.....##########.....##....##...##........#',
    '.#.......#.rrr.#......#..##..........##..#......#.rrr.#.......#.',
    '.#.......#.rrr.#........#..............#........#.rrr.#.......#.',
    '.#.......##...##.......#.#####....#####.#.......##...##.......#.',
    '..#..#########........#.#eeeee#..#eeeee#.#........#########..#..',
    '...#.........########.#.#eepee#..#eepee#.#.########.........#...',
    '....###................##eeeee#..#eeeee##................###....',
    '.......###############.#######....#######.###############.......',
    '..............######.....#.....##.....#.....######..............',
    '..........####......####..############..####......####..........',
    '........##.............##.#.#.#..#.#.#.##.............##........',
    '.......#...........###.###.#.#.##.#.#.###.###...........#.......',
    '......#......######.....#..##########..#.....######......#......',
    '.....#...####.rr......##...#........#...##......rr.####...#.....',
    '......#..............#......########......#..............#......',
    '......#............##.......#......#.......##............#......',
    '.......###.....####..........######..........####.....###.......',
    '..........#####..............#....#..............#####..........',
    '..............................####..............................',
  ]),
  // The Stopwatch: the face is the dial (hands as mustache), crown and pushers on top, ticking.
  sprite('portrait_stopwatch', { ...LED_COLORS }, [
    '.............................######.............................',
    '...............................##...............................',
    '.............#.......##.......####.......##.......#.............',
    '............#...#.....##.....######.....##.....#...#............',
    '............#..#..........###......###..........#..#............',
    '............#..#.........#.....##.....#.........#..#............',
    '............#...#.......#..#........#..#.......#...#............',
    '.............#.........######......######.........#.............',
    '......................##eeeee#....#eeeee##......................',
    '.....................#.#eepee#....#eepee#.#.....................',
    '.....................#.#eeeee#....#eeeee#.#.....................',
    '.....................#..#####......#####..#.....................',
    '.....................#....................#.....................',
    '.....................#.#.......##.......#.#.....................',
    '.....................#........###.........#.....................',
    '.....................#......###..#........#.....................',
    '.....................#..######....##......#.....................',
    '.....................#..####........##....#.....................',
    '......................#.#.............####......................',
    '......................#..................#......................',
    '.......................#.....######.....#.......................',
    '........................##.#...##...#.##........................',
    '..........................##........##..........................',
    '............................########............................',
  ]),
  // The Last Supper: the Maw itself, crowned, red-eyed, grinning a maw full of teeth.
  sprite('portrait_lastsupper', { ...LED_COLORS }, [
    '.....r##...................#...rr...#...................##r.....',
    '........#######............##..##..##............#######........',
    '..r##.....#########........###.##.###........#########.....##r..',
    '.....##......rrrrr######...##########...######rrrrr......##.....',
    '.......##....reeeerrrr######........######rrrreeeer....##.......',
    '.............reeeeeeeerrr####......####rrreeeeeeeer.............',
    'r####.........reeeppeeeeerr.#......#.rreeeeeppeeer.........####r',
    '.....####.....reeeppeeeeeeer........reeeeeeeppeeer.....####.....',
    '...............reeeeeeeeeer..........reeeeeeeeeer...............',
    '................rrrrrrrrrr............rrrrrrrrrr................',
    '................................................................',
    '................................................................',
    '...####..................................................####...',
    '.....###########................................###########.....',
    '...#.##..###.######################################.###..##.#...',
    '....#.#..##..##..###.###.###.######.###.###.###..##..##..#.#....',
    '.....#....#...#..##..##..##..##..##..##..##..##..#...#....#.....',
    '......#..##...........#.......#..#.......#...........##..#......',
    '.......#####........................................#####.......',
    '.........##..##...#...........rrrr...........#...##..##.........',
    '...........#####.##..##...#.rrrrrrrr.#...##..##.#####...........',
    '..............######.###.##rr##rr##rr##.###.######..............',
    '..................######.###r######r###.######..................',
    '........................################........................',
  ]),
  // One huge open eye: "I see you". (Single socket; the blink overlay only covers row 7.)
  sprite('face_noticed', { ...LED_COLORS }, [
    '..................#......#............#......#..................',
    '...................#.....#............#.....#...................',
    '...................#........................#...................',
    '.............#......#..##################..#......#.............',
    '..............#.....########################.....#..............',
    '...............#.######eeeeeeeeeeeeeeeeee######.#...............',
    '...............#####eeeeeeeeeeeppeeeeeeeeeee#####...............',
    '.........##..####eeeeeeeeeeeeppppppeeeeeeeeeeee####..##.........',
    '...........####eeeeeeeeeeeeepeeeeeepeeeeeeeeeeeee####...........',
    '...........##eeeeeeeeeeeeeepeeeeeeeepeeeeeeeeeeeeee##...........',
    '..........##eeeeeeeeeeeeeeppeeeppeeeppeeeeeeeeeeeeee##..........',
    '.........#eeeeeeeeeeeeeeeepeeeppppeeepeeeeeeeeeeeeeeee#.........',
    '.........#eeeeeeeeeeeeeeeepeeeppppeeepeeeeeeeeeeeeeeee#.........',
    '..........#eeeeeeeeeeeeeeeppeeeppeeeppeeeeeeeeeeeeeee#..........',
    '...........#eeeeeeeeeeeeeeepeeeeeeeepeeeeeeeeeeeeeee#...........',
    '............#eeeeeeeeeeeeeeepeeeeeepeeeeeeeeeeeeeee#............',
    '.............##eeeeeeeeeeeeeeppppppeeeeeeeeeeeeee##.............',
    '...............##eeeeeeeeeeeeeeppeeeeeeeeeeeeee##...............',
    '.................###eeeeeeeeeeeeeeeeeeeeeeee###.................',
    '....................###eeeeeeeeeeeeeeeeee###....................',
    '.......................##################.......................',
    '................................................................',
    '................................................................',
    '................................................................',
  ]),
  // face_neutral torn apart: shifted scanline band with a red fringe, snapped mouth, noise.
  sprite('face_glitch', { ...LED_COLORS }, [
    '................................................................',
    '...#............................................................',
    '..........................................................r.....',
    '.................########..............########.................',
    '..........r....##eeeeeeee##..........##eeeeeeee##...............',
    '..............#eeeeeeeeeeee#........#eeeeeeeeeeee#..............',
    '.................r#eeeeppppeeee#.......r#eeeeppppeeee#..........',
    '................r#eeeeeppppeeeee#.....r#eeeeeppppeeeee#.........',
    '.................r#eeeeppppeeee#.......r#eeeeppppeeee#..........',
    '..............#eeeeeeeeeeee#........#eeeeeeeeeeee#...........#..',
    '.............##eeeeeeee##..........##eeeeeeee##.................',
    '...............########..............########...................',
    '................................................................',
    '................................................................',
    '##.#....###.#.....##.##.......#.###.......#.#####......##.#.....',
    '............................................r...................',
    '................................................................',
    '...................................########.....................',
    '.....................########...................................',
    '................................................................',
    '........r.......................................................',
    '.....................#..............................#...........',
    '................................................................',
    '................................................................',
  ]),
  // Dead / unplugged: X-ed out eyes (no sockets), a flat mouth with a dangling end, dead LEDs.
  sprite('face_dead', { ...LED_COLORS }, [
    '................................................................',
    '................................................................',
    '................................................................',
    '..............##..........##........##..........##..............',
    '...............##........##..........##........##...............',
    '.................##....##..............##....##.................',
    '..................##..##................##..##..................',
    '....................##....................##....................',
    '...................##.#..................####...................',
    '..................##..##................##..#...................',
    '................##......##............##......##................',
    '...............##........##..........##........##...............',
    '................................................................',
    '................................................................',
    '................................................................',
    '................................................................',
    '................................................................',
    '................................................................',
    '........................###.####.####...........................',
    '.....................................#..........................',
    '......................................#.........................',
    '......................................#.........................',
    '......................................#.........................',
    '................................................................',
  ]),
];
